<template>
  <div :class="$style.formatsBlock">
    <div :class="$style.sidebar">
      <FormatGroup
        v-for="group in computedFormatGroups"
        :key="group?.fields?.name"
        :data="group"
        :selected-format-name="selectedFormatName"
        :active="activeGroupId === group?.sys?.id"
        @format-selected="handleSelectFormat"
        @toggle-format-group="toggleFormatGroup"
      />
      <div
        v-if="data?.fields?.cards && computedCardsLocation === 'Sidebar'"
        :class="$style.cardContainer"
      >
        <template v-for="card in data?.fields?.cards" :key="card?.sys?.id">
          <AsideCard v-if="card" :data="card" />
        </template>
      </div>
    </div>
    <div :class="$style.content">
      <FormatBlock
        v-if="selectedFormatName"
        :data="selectedFormat"
        :icons="computedDetailIcons"
      />
      <div
        v-if="data?.fields?.cards && computedCardsLocation === 'After'"
        :class="$style.cardContainer"
      >
        <template v-for="card in data?.fields?.cards" :key="card?.sys?.id">
          <AsideCard v-if="card" :data="card" />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeFormatGroupWithoutUnresolvableLinksResponse,
  TypeFormatsOverviewWithoutUnresolvableLinksResponse,
  TypeFormatWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const siteMeasurementStore = useSiteMeasurementStore()

// Props
const props = defineProps<{
  data: TypeFormatsOverviewWithoutUnresolvableLinksResponse
}>()

// Data
const selectedFormatName = ref(
  props?.data?.fields?.formatGroups?.[0]?.fields?.formats?.[0]?.fields?.name ||
    '',
)
const selectedFormat = ref(
  props?.data?.fields?.formatGroups?.[0]?.fields?.formats?.[0] ||
    ({} as TypeFormatWithoutUnresolvableLinksResponse),
)
const activeGroupId = ref<string | null>(
  props?.data?.fields?.formatGroups?.[0]?.sys?.id || null,
)

// Computed
const computedFormatGroups = computed(
  () =>
    props?.data?.fields
      ?.formatGroups as TypeFormatGroupWithoutUnresolvableLinksResponse[],
)

const computedCardsLocation = computed(() => {
  return siteMeasurementStore?.vWidth >= 1024 ? 'Sidebar' : 'After'
})

const computedDetailIcons = computed(() => {
  return {
    rules: props?.data?.fields?.formatDetailsIconRules?.fields?.file?.url || '',
    usage: props?.data?.fields?.formatDetailsIconUsage?.fields?.file?.url || '',
    deckSize:
      props?.data?.fields?.formatDetailsIconDeckSize?.fields?.file?.url || '',
    sideboard:
      props?.data?.fields?.formatDetailsIconSideboard?.fields?.file?.url || '',
    cardLimit:
      props?.data?.fields?.formatDetailsIconCardLimit?.fields?.file?.url || '',
    banned:
      props?.data?.fields?.formatDetailsIconBanned?.fields?.file?.url || '',
  }
})

const toggleFormatGroup = (id: string) => {
  if (activeGroupId.value === id) {
    activeGroupId.value = null
  } else {
    activeGroupId.value = id
  }
}

onMounted(() => {
  const {
    query: { format: formatQuery = '' },
  } = useRoute()

  const formatQueryAsString = Array.isArray(formatQuery)
    ? formatQuery.join()
    : formatQuery

  // If the query has a format we'll try to match it to the format groups.
  if (formatQueryAsString) {
    const formatGroupFromParam = computedFormatGroups?.value
      ?.flatMap?.((group) => group?.fields?.formats)
      .find(
        (format) =>
          format?.fields?.name?.toLowerCase?.() ===
          formatQueryAsString?.toLowerCase?.(),
      )

    if (formatGroupFromParam) {
      selectedFormatName.value = formatGroupFromParam?.fields?.name || ''
      selectedFormat.value = formatGroupFromParam
    }
  }
})

// Methods
const handleSelectFormat = (
  format: TypeFormatWithoutUnresolvableLinksResponse,
) => {
  selectedFormat.value = format
  selectedFormatName.value = format?.fields?.name || ''
}
</script>

<style lang="scss" module>
.formatsBlock {
  @include media('md') {
    align-items: flex-start;
    display: flex;
    margin: auto;
    max-width: 1317px;
    padding: 58px 0;
  }
}

.sidebar {
  @include media('md') {
    flex: 0 0 340px;
    margin-right: 64px;
  }
}

.cardContainer {
  display: grid;
  gap: 19px;
}

.content {
  @include media('md') {
    flex: 1 1 auto;
    max-width: 912px;
  }
}
</style>
